import {
  ApolloClient,
  FetchResult,
  InMemoryCache,
  Observable,
  ServerError,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import fetch from 'cross-fetch';
import { t } from 'i18next';

import { ProjectObject } from '@appTypes/resources/project';
import { NotificationService } from '@services/notification/Notification.service';
import {
  getUserAuthToken,
  getUserRefreshToken,
  removeUserToken,
} from '@utils/userToken';

import { createSessionRefresher } from './sessionRefresh';
import {
  RefreshSessionTokenDocument,
  RefreshSessionTokenMutation,
  FetchDictionaryDocument,
} from './types';
import { createUploadFetch } from './uploadFetch';
import { authTokenSharedVar } from './vars';

/**
 * `apollo-upload-client` names each multipart part `1..n` and writes the `map`
 * before it has seen a single file, so the part names it produces carry no
 * meaning. The backend needs an image's part name to be its `imageRef`, and
 * this is the last point at which the assembled body can still be corrected —
 * the library exposes no other hook past the point the `map` is written.
 *
 * The shape this produces — field name `<imageRef>`, `filename`
 * `<imageRef>.<ext>` — is the one `cropink-figma-plugin` sends successfully
 * (`src/ui/services/api/GraphQL.service.ts` there).
 */
const httpLink = createUploadLink({
  uri: import.meta.env.VITE_GRAPHQL_API_URL,
  fetch: createUploadFetch(fetch),
});

const authLink = setContext((_, client) => {
  const { headers } = client;

  const sharedToken = authTokenSharedVar()?.token;

  const authorization =
    headers?.authorization || `Bearer ${sharedToken || getUserAuthToken()}`;

  return {
    headers: {
      ...headers,
      authorization,
    },
  };
});

const errorLink = onError(({ networkError, operation, forward }) => {
  if (!networkError) return undefined;

  const { statusCode, message } = networkError as ServerError;

  if (statusCode === 500) {
    NotificationService.send(t('ApolloErrors.StatusCode500'), {
      variant: 'negative',
    });

    return undefined;
  }

  const redirectToMaintenance = (): void => {
    if (!window.location.pathname.includes('maintenance')) {
      window.sessionStorage.setItem('maintenance', 'true');
      window.location.href = '/maintenance';
    }
  };

  const checkStatusCode = (): void | Observable<FetchResult> => {
    switch (statusCode) {
      case 503:
      case 502:
      case 423:
        return redirectToMaintenance();
      case 401:
        return handleUnauthorized({ operation, forward });

      default:
        if (
          message === 'Network request failed' ||
          message === 'Failed to fetch'
        ) {
          return redirectToMaintenance();
        }
    }

    return undefined;
  };

  // If status code is 5xx (API doesn't respond), try to query dictionary to check if backend is reachable
  if (
    (statusCode > 500 && statusCode < 600) ||
    (statusCode === undefined && message === 'Network request failed')
  ) {
    try {
      client.query({ query: FetchDictionaryDocument });

      return undefined;
    } catch (error) {
      return checkStatusCode();
    }
  } else {
    return checkStatusCode();
  }
});

type HandleUnauthorizedOptions = Pick<ErrorResponse, 'operation' | 'forward'>;

/**
 * How long a session refresh may take. Neither Apollo nor `fetch` gives up on
 * a request by itself, and every 401 in every tab waits on the refresh (and on
 * its Web Lock) — one that hangs would hold them all with no end. Past this it
 * fails like any other refresh.
 */
const REFRESH_TIMEOUT_MS = 20_000;

const refreshTimeoutSignal = (): AbortSignal | undefined =>
  typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REFRESH_TIMEOUT_MS)
    : undefined;

const refreshSession = createSessionRefresher({
  requestToken: (refreshToken) =>
    client
      .mutate<RefreshSessionTokenMutation>({
        mutation: RefreshSessionTokenDocument,
        variables: { input: { refreshToken } },
        // The expired session token must not go along with the refresh.
        context: {
          headers: { authorization: 'Bearer ' },
          fetchOptions: { signal: refreshTimeoutSignal() },
        },
      })
      .then(({ data }) => {
        const sessionToken = data?.refreshSessionToken?.sessionToken;
        if (!sessionToken)
          throw new Error('Session refresh returned no token.');

        return sessionToken;
      }),
  onFailure: () => {
    client.clearStore();
    removeUserToken();
  },
});

const handleUnauthorized = ({
  operation,
  forward,
}: HandleUnauthorizedOptions): Observable<FetchResult> | undefined => {
  if (operation.operationName === 'RefreshSessionToken') return undefined;
  if (!getUserRefreshToken()) return undefined;

  const oldHeaders = operation.getContext().headers;

  const failedAuthToken = `${oldHeaders?.authorization ?? ''}`.replace(
    /^Bearer\s*/,
    '',
  );

  return new Observable<FetchResult>((observer) => {
    let subscription: { unsubscribe: () => void } | undefined;

    refreshSession(failedAuthToken)
      .then((token) => {
        // Unsubscribed while the refresh was in flight — an unmounted
        // component, a cancelled query: nobody reads the replay. Not reached
        // through onError in Apollo 3.14.1: its cleanup unsubscribes the
        // original request twice and never this observable
        // (`link/error/index.js`, `retriedSub` branch), so the replay still
        // goes out unread. Harmless until Apollo fixes that, and right after.
        if (observer.closed) return;

        operation.setContext({
          headers: { ...oldHeaders, authorization: `Bearer ${token}` },
        });

        subscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((err) => observer.error(err));

    return () => subscription?.unsubscribe();
  });
};

const graphqlErrorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((err) => {
      console.error(err);

      if (
        err.extensions?.code === 'CRP-1601' &&
        !err.path?.includes('createSubscriptionCheckoutSession') &&
        !err.path?.includes('cancelSubscription')
      ) {
        NotificationService.send(err.message, { variant: 'negative' });
      } else if (err.extensions?.code === 'CRP-0903') {
        // eslint-disable-next-line no-param-reassign
        (err as { message: string }).message = t('ApolloErrors.CRP-0903');
      } else if (err.extensions?.code === 'CRP-0911') {
        // eslint-disable-next-line no-param-reassign
        (err as { message: string }).message = t('ApolloErrors.CRP-0911');
      }
    });
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Team: {
      keyFields: ['teamId'],
    },
    Project: {
      keyFields: ['projectId'],
      fields: {
        projectObjects: {
          merge(_, incoming) {
            return incoming.map((po: ProjectObject) => ({
              ...po,
              projectObjectBehaviors: [...po.projectObjectBehaviors].sort(
                (a, b) => a.ordering - b.ordering,
              ),
            }));
          },
        },
      },
    },
    Design: {
      keyFields: ['designId'],
    },
  },
});

export const client = new ApolloClient({
  link: from([authLink, errorLink, graphqlErrorLink, httpLink]),
  cache,
});

client.onClearStore(() => {
  client.stop();

  return new Promise((resolve) => {
    resolve(true);
  });
});
